import React, { useCallback, useState } from 'react';
import { Login } from '../auth/Login';
import { Landing } from '../screens/public/landing';
import { Signup } from '../screens/public/signup';
import { About } from '../screens/public/about';
import { Privacy } from '../screens/public/privacy';
import { Contact } from '../screens/public/contact';
import { DeleteAccount } from '../screens/public/delete-account';
import { Terms } from '../screens/public/terms';
import '../styles/style.css';

type PublicView =
  | 'landing'
  | 'login'
  | 'signup'
  | 'about'
  | 'privacy'
  | 'terms'
  | 'contact'
  | 'delete-account';

export interface PublicScreenRoute {
  name: string;
  screen: React.FC<{ onBack: () => void }>;
  route: string;
}

// Public marketing/legal pages that must also render correctly on a fresh,
// unauthenticated direct URL load (e.g. the Play Store's privacy-policy URL
// checker) — not just via in-app click-through below. Consumed by App.tsx's
// direct-route check.
export const publicScreenRoute: PublicScreenRoute[] = [
  { name: "About Us", screen: About, route: "/about-us" },
  { name: "Contact Us", screen: Contact, route: "/contact-us" },
  { name: "Privacy Policy", screen: Privacy, route: "/privacy-policy" },
  { name: "Terms of Service", screen: Terms, route: "/terms-of-service" },
  { name: "Delete Your Account", screen: DeleteAccount, route: "/delete-account" },
];

// Screens shown to unauthenticated visitors: a marketing landing page, the
// sign-in form, self-serve signup, and static content pages.
export const PublicScreens: React.FC = () => {
  const [view, setViewState] = useState<PublicView>('landing');
  const [planId, setPlanId] = useState<string | undefined>(undefined);

  const goTo = useCallback((next: PublicView) => {
    setViewState(next);
  }, []);

  // Used by each screen's own "back" link/button.
  const back = () => setViewState('landing');

  if (view === 'login') return <Login onBack={back} />;
  if (view === 'signup')
    return (
      <Signup
        planId={planId}
        onBack={back}
        onShowPrivacy={() => goTo('privacy')}
        onShowTerms={() => goTo('terms')}
      />
    );
  if (view === 'about') return <About onBack={back} />;
  if (view === 'privacy') return <Privacy onBack={back} />;
  if (view === 'terms') return <Terms onBack={back} />;
  if (view === 'contact') return <Contact onBack={back} />;
  if (view === 'delete-account') return <DeleteAccount onBack={back} />;

  return (
    <Landing
      onSignIn={() => goTo('login')}
      onBuy={(id) => {
        setPlanId(id);
        goTo('signup');
      }}
      onShowPage={(page) => goTo(page)}
    />
  );
};
